import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Clock, PartyPopper, Facebook, Navigation } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { fetchEvents, fetchSchedule } from "@/lib/api";

const STATUS_STYLE = {
  SERVING: { cls: "bg-cb-teal text-cb-black", dot: "bg-green-500 animate-blinker", label: "Serving This Week" },
  CLOSED: { cls: "bg-cb-black text-white", dot: "bg-white/50", label: "Closed" },
  BOOKED: { cls: "bg-cb-pink text-white", dot: "bg-white animate-blinker", label: "Fully Booked" },
};

const TILTS = ["-rotate-1", "rotate-1", "rotate-0", "-rotate-2", "rotate-2", "-rotate-1", "rotate-1"];

export default function Truck() {
  const { data: stops = [], isLoading } = useQuery({ queryKey: ["schedule"], queryFn: fetchSchedule });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: fetchEvents });

  const todayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][new Date().getDay()];
  const todayStop = stops.find((s) => s.day === todayName && s.map_query);
  const mapStop = todayStop || stops.find((s) => s.status === "SERVING" && s.map_query);
  const isToday = !!todayStop;

  return (
    <div data-testid="truck-page">
      <section className="bg-cb-black py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 halftone opacity-20" />
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <span className="cb-badge bg-cb-pink text-white mb-6 inline-block" data-testid="truck-badge">Hot Wheels, Hotter Rolls</span>
          <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="truck-headline">
            <LineReveal
              lines={[
                <span key="a" className="text-outline-teal inline-block -rotate-1">Find The</span>,
                <span key="b" className="text-outline-yellow inline-block rotate-1">Crazy Buns Truck</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-5 text-white/75 font-bold text-base md:text-lg max-w-xl mx-auto">
              Check the weekly route below. Times can shift with the weather (and the cinnamon supply) —
              follow us on Facebook for same-day updates.
            </p>
          </Reveal>
        </div>
      </section>

      {mapStop && (
        <section data-testid="live-map-section" className="bg-cb-yellow border-b-3 border-cb-black py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <span className="cb-badge bg-cb-pink text-white mb-4 inline-flex items-center gap-2 !rotate-0">
                <span className="w-2 h-2 rounded-full bg-white animate-blinker" />
                {isToday ? "Serving Today" : "Next Stop"}
              </span>
              <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight" data-testid="live-map-heading">
                {isToday ? "Catch The Truck Today!" : "Catch The Truck Next!"}
              </h2>
              <p className="mt-4 font-bold text-cb-black/70 text-base md:text-lg">{mapStop.day} · {mapStop.time}</p>
              <p className="font-display font-semibold uppercase text-xl mt-1">{mapStop.location}</p>
              <p className="font-bold text-cb-black/60 text-sm mt-1">{mapStop.address}</p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapStop.map_query)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="live-map-directions-btn"
                className="cb-btn cb-btn-black mt-6 !text-sm"
              >
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="cb-card overflow-hidden rotate-1 hover:rotate-0 transition-transform duration-300">
                <iframe
                  title="Crazy Buns truck location map"
                  data-testid="live-map-iframe"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(mapStop.map_query)}&output=embed`}
                  className="w-full h-80 sm:h-96 border-0"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-cb-cream halftone-light py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4">
          {isLoading ? (
            <p className="text-center font-display font-semibold uppercase text-lg animate-pulse" data-testid="schedule-loading">Rolling up the route...</p>
          ) : (
            <div className="space-y-6" data-testid="schedule-list">
              {stops.map((s, i) => {
                const st = STATUS_STYLE[s.status] || STATUS_STYLE.SERVING;
                return (
                  <Reveal key={s.day} delay={i * 0.06}>
                    <div
                      data-testid={`truck-schedule-card-${s.day.toLowerCase()}`}
                      className={`cb-card cb-card-hover p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5 ${TILTS[i % TILTS.length]} hover:rotate-0`}
                    >
                      <div className="shrink-0 flex sm:flex-col items-center gap-3 sm:gap-1 sm:w-28">
                        <span className="font-display font-bold uppercase text-xl sm:text-2xl">{s.day.slice(0, 3)}</span>
                        <span className={`cb-badge ${st.cls} !rotate-0 inline-flex items-center gap-1.5`}>
                          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                          {s.status}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h2 className="font-display font-semibold uppercase text-xl sm:text-2xl">{s.location}</h2>
                        <p className="font-bold text-cb-black/60 text-sm mt-1">{s.address}</p>
                        <p className="inline-flex items-center gap-1.5 font-extrabold text-sm mt-2 text-cb-pink">
                          <Clock className="w-4 h-4" /> {s.time}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {s.map_query ? (
                          <a
                            data-testid={`truck-directions-${s.day.toLowerCase()}`}
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.map_query)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cb-btn cb-btn-teal !px-5 !py-2.5 !text-xs sm:!text-sm"
                          >
                            <Navigation className="w-4 h-4" /> Directions
                          </a>
                        ) : s.status === "BOOKED" ? (
                          <Link to="/book" data-testid={`truck-book-${s.day.toLowerCase()}`} className="cb-btn cb-btn-pink !px-5 !py-2.5 !text-xs sm:!text-sm">
                            <PartyPopper className="w-4 h-4" /> Book This Day
                          </Link>
                        ) : (
                          <span className="cb-badge bg-white text-cb-black !rotate-0">See You Tuesday</span>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}

          <Reveal delay={0.2} className="mt-12">
            <div className="cb-card bg-cb-yellow p-7 flex flex-col sm:flex-row items-center gap-5 -rotate-1 hover:rotate-0" data-testid="truck-facebook-note">
              <div className="w-14 h-14 shrink-0 rounded-full border-3 border-cb-black bg-white flex items-center justify-center shadow-hard-sm">
                <Facebook className="w-6 h-6 text-cb-pink" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-display font-semibold uppercase text-lg">Today's live location?</h3>
                <p className="font-bold text-sm text-cb-black/70">
                  Follow <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="underline decoration-cb-pink decoration-2 hover:text-cb-pink transition-colors" data-testid="truck-facebook-link">Crazy Buns on Facebook</a> — we post the day's stop and any schedule changes every morning.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="events" data-testid="upcoming-events-section" className="bg-cb-pink border-y-3 border-cb-black py-16 sm:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Reveal>
            <span className="cb-badge bg-cb-yellow text-cb-black mb-4 inline-block">Save The Date</span>
            <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
              Upcoming Events
            </h2>
            <p className="mt-4 text-white/85 font-bold text-base md:text-lg max-w-2xl mx-auto">
              Catch the truck at these community blowouts — extra rolls on board, extra smiles guaranteed.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-6" data-testid="upcoming-events-grid">
            {events.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.1}>
                <div data-testid={`upcoming-event-${i}`} className={`cb-card cb-card-hover bg-white p-6 ${TILTS[i]} hover:rotate-0`}>
                  <span className="cb-badge bg-cb-teal text-cb-black">{e.date}</span>
                  <h3 className="font-display font-semibold uppercase text-lg mt-4">{e.name}</h3>
                  <p className="font-bold text-sm text-cb-black/60 mt-1 flex items-center justify-center gap-1">
                    <MapPin className="w-4 h-4 text-cb-pink" /> {e.place}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <Link to="/book" data-testid="truck-book-cta" className="cb-btn cb-btn-yellow mt-12">
              <PartyPopper className="w-5 h-5" /> Want Us At Your Event? Book Now
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee bg="bg-cb-yellow" text="text-cb-black" />
    </div>
  );
}
